import React, { useState, useEffect } from 'react';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';

export default function CourseFilters({
  onFilterChange,
  subjectOptions,
  subjectMapping,
  termOptions,
}) {
  const [filters, setFilters] = useState({
    term: [],
    subject: [],
    showOnlyQRatings: false,
    showOnlyScheduled: false,
    sortByWorkload: 'none',
    sortByQScore: 'none',
    days: {
      M: false,
      T: false,
      W: false,
      Th: false,
      F: false,
    },
  });

  const [openDropdown, setOpenDropdown] = useState(null);

  // Update parent component when filters change
  useEffect(() => {
    onFilterChange(filters);
  }, [filters, onFilterChange]);

  // Handler for dropdown selection changes
  const handleDropdownChange = (type, option, selected) => {
    setFilters((prev) => {
      const newArray = selected
        ? [...prev[type], option]
        : prev[type].filter((item) => item !== option);

      return { ...prev, [type]: newArray };
    });
  };

  // Handler for day toggles
  const handleDayToggle = (day) => {
    setFilters((prev) => ({
      ...prev,
      days: {
        ...prev.days,
        [day]: !prev.days[day],
      },
    }));
  };

  // Clear all selections for a specific filter type
  const clearAll = (type) => {
    setFilters((prev) => ({ ...prev, [type]: [] }));
  };

  // Select all options for a specific filter type
  const selectAll = (type, options) => {
    setFilters((prev) => ({ ...prev, [type]: [...options] }));
  };

  // Get subject display name
  const getSubjectDisplayName = (code) => {
    const fullName = subjectMapping.get(code);
    return fullName || code;
  };

  // Dropdown label for term and subject
  const getDropdownLabel = (type, options) => {
    if (filters[type].length === 0) return type === 'term' ? 'All Terms' : 'All Subjects';
    if (filters[type].length === options.length) return 'All Selected';
    if (filters[type].length === 1) {
      if (type === 'subject') {
        return getSubjectDisplayName(filters[type][0]);
      }
      return filters[type][0];
    }
    return `${filters[type].length} selected`;
  };

  const DropdownMenu = ({ type, options, label, className = '' }) => {
    const isOpen = openDropdown === type;


    return (
      <div className={`relative ${className}`}>
        <button
          onClick={() => setOpenDropdown(isOpen ? null : type)}
          className="btn btn-secondary flex items-center space-x-2 min-w-32"
        >
          <span>{getDropdownLabel(type, options)}</span>
          <ChevronDownIcon className="h-4 w-4" />
        </button>

        {isOpen && (
          <div className="absolute z-50 mt-2 w-72 bg-white dark:bg-dark-200 rounded-lg shadow-lg border border-gray-200 dark:border-dark-400 max-h-96 overflow-y-auto">
            {/* No options message */}
            {(!options || options.length === 0) && (
              <div className="px-4 py-2 text-sm text-gray-500 dark:text-dark-500 italic">
                No {type} options available
              </div>
            )}
            {/* Selected items */}
            {filters[type].length > 0 && (
              <>
                <div className="px-4 py-2 bg-gray-50 dark:bg-dark-300 border-b border-gray-200 dark:border-dark-400">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700 dark:text-dark-700">
                      Selected
                    </span>
                    <button
                      onClick={() => clearAll(type)}
                      className="text-xs text-red-600 hover:text-red-500"
                    >
                      Clear all
                    </button>
                  </div>
                </div>
                <div className="py-1">
                  {filters[type].map((item) => (
                    <button
                      key={`selected-${item}`}
                      onClick={() => handleDropdownChange(type, item, false)}
                      className="w-full flex items-center justify-between px-4 py-2 text-sm text-gray-700 dark:text-dark-700 hover:bg-gray-50 dark:hover:bg-dark-300"
                    >
                      <span className="truncate">
                        {type === 'subject' ? getSubjectDisplayName(item) : item}
                      </span>
                      <CheckIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* Unselected items */}
            {options && options.length > 0 && (
              <>
                <div className="px-4 py-2 bg-gray-50 dark:bg-dark-300 border-b border-t border-gray-200 dark:border-dark-400">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700 dark:text-dark-700">
                      Available
                    </span>
                    <button
                      onClick={() => selectAll(type, options)}
                      className="text-xs text-green-600 hover:text-green-500"
                    >
                      Select all
                    </button>
                  </div>
                </div>
                <div className="py-1">
                  {options
                    .filter((item) => !filters[type].includes(item))
                    .map((item) => (
                      <button
                        key={`unselected-${item}`}
                        onClick={() => handleDropdownChange(type, item, true)}
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-dark-700 hover:bg-gray-50 dark:hover:bg-dark-300 text-left"
                      >
                        <span className="truncate">
                          {type === 'subject' ? getSubjectDisplayName(item) : item}
                        </span>
                      </button>
                    ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="bg-white dark:bg-dark-200 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-dark-400">
      <div className="flex flex-wrap items-center gap-4">
        {/* Term Filter */}
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-gray-700 dark:text-dark-700">
            Term:
          </span>
          <DropdownMenu type="term" options={termOptions} label="Term" />
        </div>

        {/* Subject Filter */}
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-gray-700 dark:text-dark-700">
            Subject:
          </span>
          <DropdownMenu type="subject" options={subjectOptions} label="Subject" />
        </div>

        {/* Q Ratings Filter */}
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.showOnlyQRatings}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                showOnlyQRatings: e.target.checked,
              }))
            }
            className="w-4 h-4 text-accent-primary bg-white dark:bg-dark-200 border-gray-300 dark:border-dark-400 rounded focus:ring-accent-primary focus:ring-2"
          />
          <span className="text-sm text-gray-700 dark:text-dark-700">
            Q-rated only
          </span>
        </label>

        {/* Scheduled Courses Filter */}
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.showOnlyScheduled}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                showOnlyScheduled: e.target.checked,
              }))
            }
            className="w-4 h-4 text-accent-primary bg-white dark:bg-dark-200 border-gray-300 dark:border-dark-400 rounded focus:ring-accent-primary focus:ring-2"
          />
          <span className="text-sm text-gray-700 dark:text-dark-700">
            Scheduled only
          </span>
        </label>
      </div>

      {/* Second row with days and sorting */}
      <div className="flex flex-wrap items-center justify-between gap-4 mt-4 pt-4 border-t border-gray-200 dark:border-dark-400">
        {/* Days Filter */}
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-gray-700 dark:text-dark-700">
            Days:
          </span>
          <div className="flex space-x-1">
            {Object.keys(filters.days).map((day) => (
              <button
                key={day}
                onClick={() => handleDayToggle(day)}
                className={`w-8 h-8 text-xs font-medium rounded transition-colors ${
                  filters.days[day]
                    ? 'bg-accent-primary text-white'
                    : 'bg-gray-100 dark:bg-dark-300 text-gray-700 dark:text-dark-700 hover:bg-gray-200 dark:hover:bg-dark-400'
                }`}
              >
                {day}
              </button>
            ))}
          </div>
        </div>

        {/* Sorting Controls */}
        <div className="flex items-center space-x-4">
          {/* Workload Sorting */}
          <div className="relative">
            <select
              value={filters.sortByWorkload}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  sortByWorkload: e.target.value,
                  sortByQScore: e.target.value !== 'none' ? 'none' : prev.sortByQScore,
                }))
              }
              className="btn btn-secondary pr-8 appearance-none"
            >
              <option value="none">Sort by Workload</option>
              <option value="low-to-high">Low to High</option>
              <option value="high-to-low">High to Low</option>
            </select>
            <ChevronDownIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 pointer-events-none" />
          </div>

          {/* Q-Score Sorting */}
          <div className="relative">
            <select
              value={filters.sortByQScore}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  sortByQScore: e.target.value,
                  sortByWorkload: e.target.value !== 'none' ? 'none' : prev.sortByWorkload,
                }))
              }
              className="btn btn-secondary pr-8 appearance-none"
            >
              <option value="none">Sort by Q-Score</option>
              <option value="high-to-low">High to Low</option>
              <option value="low-to-high">Low to High</option>
            </select>
            <ChevronDownIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 pointer-events-none" />
          </div>
        </div>
      </div>
    </div>
  );
}